import { isEmptyString } from '@racemap/utilities/functions/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// Shared state for batching default values
const pendingDefaults = new Map<string, any>();
let batchTimeoutId: number | null = null;

// Parse parameter from URL
const getParamValue = (params: URLSearchParams, key: string): string | Array<string> | null => {
  const value = params.get(key);
  return value === null ? null : value.includes(',') ? value.split(',') : value;
};

type OnlyArrays<T> = T extends Array<any> ? T : never;
type Result<B extends boolean | undefined, T> = B extends true
  ? OnlyArrays<T>
  : Exclude<T, Array<any>>;

export function useURLState<
  T extends Array<string> | string | null,
  M extends boolean | undefined = false,
>(key: string, isMultiple?: M, defaultValue?: T): [Result<M, T>, (value: Result<M, T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialized = useRef(false);

  // Get current value from URL or use default
  const rawValue =
    getParamValue(searchParams, key) || defaultValue || (isMultiple ? [] : '') || null;
  let value: T | Array<string> | string | null = rawValue;
  if (isMultiple && typeof value === 'string') {
    value = [value] as T extends Array<string> ? T : never;
  }

  // Handler to update URL parameter
  const setValue = useCallback(
    (newValue: Result<typeof isMultiple, T>) => {
      setSearchParams((params) => {
        if (newValue == null || isEmptyString(newValue)) {
          params.delete(key);
        } else if (Array.isArray(newValue)) {
          params.set(key, newValue.join(','));
        } else {
          params.set(key, newValue);
        }
        return params;
      });
    },
    [key, setSearchParams],
  );

  // Process all pending defaults in a single batch
  const processBatch = useCallback(() => {
    if (pendingDefaults.size === 0) return;

    setSearchParams(
      (params) => {
        pendingDefaults.forEach((value, key) => {
          if (!params.has(key)) {
            if (Array.isArray(value)) {
              params.set(key, value.join(','));
            } else if (value != null && !isEmptyString(value)) {
              params.set(key, value);
            }
          }
        });
        pendingDefaults.clear();
        return params;
      },
      { replace: true },
    );

    batchTimeoutId = null;
  }, [setSearchParams]);

  // Set up default value and handle initialization
  useEffect(() => {
    // Mark as initialized if param already exists in URL
    if (searchParams.has(key)) {
      initialized.current = true;
      return;
    }

    // Queue default value if needed
    if (!initialized.current && defaultValue != null) {
      initialized.current = true;
      // Store the properly formatted default value
      const valueToStore =
        isMultiple && !Array.isArray(defaultValue) ? [defaultValue] : defaultValue;
      pendingDefaults.set(key, valueToStore);

      // Schedule batch processing if not already scheduled
      if (batchTimeoutId === null) {
        batchTimeoutId = window.setTimeout(processBatch, 0);
      }
    }
  }, [key, defaultValue, searchParams, isMultiple, processBatch]);

  return [value as Result<M, T>, setValue];
}
